import Icon from '../../../components/AppIcon';
import type { Feature } from '../types';

const FeaturesSection = () => {
  const features: Feature[] = [
    {
      id: "1",
      icon: "Brain",
      title: "Natural Language Understanding",
      description: "Advanced NLP powered by OpenAI GPT-4 that comprehends context, intent, and sentiment in customer conversations.",
      benefits: [
        "Understands complex queries and context",
        "Handles multiple languages seamlessly",
        "Learns from every interaction"
      ]
    },
    {
      id: "2",
      icon: "MessageSquare",
      title: "Multi-Channel Integration",
      description: "Deploy your AI chatbot across website, WhatsApp, Facebook Messenger, and other platforms from a single dashboard.",
      benefits: [
        "Unified conversation history",
        "Consistent responses across channels",
        "Centralized management interface"
      ]
    },
    {
      id: "3",
      icon: "Workflow",
      title: "Smart Workflow Automation",
      description: "Seamlessly integrate with your existing systems to automate ticket creation, order tracking, and appointment scheduling.",
      benefits: [
        "CRM and helpdesk integration",
        "Automated task assignment",
        "Real-time data synchronization"
      ]
    },
    {
      id: "4",
      icon: "Users",
      title: "Human Handoff",
      description: "Intelligent escalation to human agents when needed, with full conversation context and customer history.",
      benefits: [
        "Smooth transition to live agents",
        "Complete conversation context",
        "Priority-based routing"
      ]
    },
    {
      id: "5",
      icon: "BarChart3",
      title: "Analytics & Insights",
      description: "Comprehensive dashboards showing conversation metrics, customer satisfaction, and bot performance analytics.",
      benefits: [
        "Real-time performance monitoring",
        "Customer sentiment analysis",
        "Continuous improvement insights"
      ]
    },
    {
      id: "6",
      icon: "Shield",
      title: "Enterprise Security",
      description: "Bank-level encryption, GDPR compliance, and secure data handling to protect customer information.",
      benefits: [
        "End-to-end encryption",
        "Compliance with data regulations",
        "Regular security audits"
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Sparkles" size={16} />
            <span>Powerful Features</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Everything You Need for Intelligent Automation
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our AI chatbot platform combines cutting-edge technology with practical business solutions
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-all duration-fast hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Icon name={feature.icon} size={28} className="text-primary" />
              </div>
              
              <h3 className="text-xl font-semibold text-text-primary mb-3">
                {feature.title}
              </h3>
              
              <p className="text-text-secondary mb-4 leading-relaxed">
                {feature.description}
              </p>
              
              <ul className="space-y-2">
                {feature.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;